import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Lookup } from "@/models/Lookup";
import { auditRunReport } from "@/services/auditService";
import { getGeneralAccessReport } from "@/services/reportService";
import { getErrorMessage, showError } from "@/services/utilities/ServiceError";
import { ListQuery } from "@/models/ListQuery";
import GeneralAccessReportColumns, { GeneralAccessReportChildColumns } from "@/models/ColumnDefinitions/GeneralAccessReportColumns";
import { DEF_PG_SIZE_REPORTS } from "@/constants/maximum-page-size";
import { Filter } from "@/models/Filter";
import { toast } from "react-toastify";
import { REPORTS_GENERAL_ACCESS } from "@/constants/menu-types";
import { getUserFiltersByView } from "@/services/userPreferenceService";
import { FILTER_ACCESS_GROUP, FILTER_ACCESS_TIME_PATTERN, FILTER_COMPANIES, FILTER_DEPARTMENTS, FILTER_SITES, FILTER_TAGHOLDERS } from "@/constants/filter-types";
import LookupTypes from "@/models/LookupTypes";
import { getCompanyNamesWithPagination } from "@/services/companyService";
import CompanySearch from "@/models/search/CompanySearch";
import { getDepartmentNamesWithPagination } from "@/services/departmentService";
import DepartmentSearch from "@/models/search/DepartmentSearch";
import { getEnrolmentNamesWithPagination } from "@/services/enrolmentService";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";
import { getSiteNamesWithPagination } from "@/services/siteService";
import SiteSearch from "@/models/search/SiteSearch";
import { getAccessGroupsNamesWithPagination } from "@/services/accessGroupService";
import AccessGroupSearch from "@/models/search/AccessGroupSearch";
import GeneralAccessReportSearch from "@/models/search/GeneralAccessReportSearch.ts";

interface GeneralAccessReportProps {
    reportId: string;
}

const GeneralAccessReport: FC<GeneralAccessReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => state.userInfo.id);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "accessGroupId",
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessGroupsNamesWithPagination(null, listQuery),
            searchAccessor: AccessGroupSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect:true
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "companyId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.company),
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "departmentId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.department),
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "masterId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "accessGroupId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.accessGroup),
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            case "siteId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
            default:
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    isStatic: true,
                    selected: [{ label: translate.byKey(TranslationKeys.all), value: "*", ignore: true }],
                };
        }
    };

    const initialiseUserPreferences = async (setDetails: any) => {
        const staticFilterKeys: string[] = ["companyId", "departmentId", "masterId", "accessGroupId", "siteId"];
        let customFilter: Filter[] = [];

        try {
            const response = await getUserFiltersByView(userLoginId, REPORTS_GENERAL_ACCESS, reportId);

            if (response.find(filter => filter.filterId === FILTER_COMPANIES) === undefined) {
                customFilter.push(await getOriginalStaticFilter("companyId"));
            }
            if (response.find(filter => filter.filterId === FILTER_DEPARTMENTS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("departmentId"));
            }
            if (response.find(filter => filter.filterId === FILTER_TAGHOLDERS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
            if (response.find(filter => filter.filterId === FILTER_ACCESS_GROUP) === undefined) {
                customFilter.push(await getOriginalStaticFilter("accessGroupId"));
            }
            if (response.find(filter => filter.filterId === FILTER_SITES) === undefined) {
                customFilter.push(await getOriginalStaticFilter("siteId"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, REPORTS_GENERAL_ACCESS, customFilter, reportId, staticFilterKeys);
        }
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getGeneralAccessReport(translate, toast.warn, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    };

    return <TableContextProvider
            maxPageSize={DEF_PG_SIZE_REPORTS}
            dataMethod={getDataMethod}
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            tableDescriptor={GeneralAccessReportColumns}
            searchDescriptor={GeneralAccessReportSearch}
            useFilter
            useSort
            usePagination
            useSearch
            useTable
            lookup={lookup}
            getOriginalStaticFilter={getOriginalStaticFilter}
            loadDataOnDemand
            runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
            childColumns={GeneralAccessReportChildColumns}>
            <TableTab
                title={''}
                reportTitle={translate.byKey(TranslationKeys.generalAccessReport)}
                selectRows={false}
                cyName="GeneralAccessReport"
                useGroupedRows
                highlightExpandedParent
                showFilterMenu={true}
                childTableHeading={translate.byKey(TranslationKeys.accessGroups)} />
        </TableContextProvider>
};

export default GeneralAccessReport;
